import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, AlertCircle, Smartphone, Monitor } from "lucide-react";
import { toast } from "sonner";
import { api, ADMIN_TOKEN_KEY } from "@/lib/api";
import { SIM_LABELS } from "@/lib/format";

const isoDay = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const daysAgo = (n) => isoDay(new Date(Date.now() - n * 86400000));
const pct = (v) => (v === null || v === undefined ? "—" : `${String(v).replace(".", ",")} %`);
const headers = () => ({ "X-Admin-Session": localStorage.getItem(ADMIN_TOKEN_KEY) });

const COLS = [
  ["arrivee", "Arrivées"], ["commencee", "Commencées"], ["terminee", "Terminées"], ["lead", "Leads"],
  ["taux_commencees", "Arr.→Com."], ["taux_terminees", "Com.→Ter."], ["taux_leads", "Ter.→Leads"],
];

function FunnelTable({ title, rows, label, firstCol, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="text-xs font-bold tracking-[0.18em] uppercase text-[#C84C2C] mb-3">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-[#8C7A70] bg-white border border-[#E6DDD4] rounded-2xl p-4">Aucune donnée sur la période.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#E6DDD4] rounded-2xl">
          <table className="w-full text-xs whitespace-nowrap">
            <thead>
              <tr className="text-left text-[#8C7A70] border-b border-[#E6DDD4]">
                <th className="px-3 py-2.5 font-semibold sticky left-0 bg-white">{firstCol}</th>
                {COLS.map(([k, l]) => <th key={k} className="px-3 py-2.5 font-semibold text-right">{l}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid={`${testid}-row-${i}`} className="border-b last:border-0 border-[#F0E9E0] text-[#231C18]">
                  <td className="px-3 py-2.5 font-medium sticky left-0 bg-white">{label(r)}</td>
                  {COLS.map(([k]) => (
                    <td key={k} data-testid={`${testid}-row-${i}-${k}`} className="px-3 py-2.5 text-right tabular-nums">
                      {k.startsWith("taux") ? pct(r[k]) : r[k]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DeviceSplit({ devices }) {
  const total = (k) => devices.mobile[k] + devices.desktop[k];
  const share = (d, k) => (total(k) ? Math.round((devices[d][k] / total(k)) * 100) : 0);
  return (
    <section data-testid="traffic-devices">
      <h2 className="text-xs font-bold tracking-[0.18em] uppercase text-[#C84C2C] mb-3">Mobile / desktop</h2>
      <div className="grid grid-cols-2 gap-3">
        {[["mobile", "Mobile", Smartphone], ["desktop", "Desktop", Monitor]].map(([d, l, Icon]) => (
          <div key={d} data-testid={`traffic-device-${d}`} className="bg-white border border-[#E6DDD4] rounded-2xl p-4">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-[#231C18]">
              <Icon strokeWidth={1.5} className="h-4 w-4 text-[#C84C2C]" /> {l}
            </p>
            <p className="mt-2 text-xs text-[#8C7A70]">
              Arrivées <span className="font-semibold text-[#231C18]">{devices[d].arrivee}</span> ({share(d, "arrivee")} %)
            </p>
            <p className="text-xs text-[#8C7A70]">
              Leads <span className="font-semibold text-[#231C18]">{devices[d].lead}</span> ({share(d, "lead")} %)
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function TrafficDashboard({ onUnauthorized }) {
  const [preset, setPreset] = useState("7");
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(isoDay(new Date()));
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  const choosePreset = (p) => {
    setPreset(p);
    if (p !== "custom") {
      setStart(daysAgo(Number(p) - 1));
      setEnd(isoDay(new Date()));
    }
  };

  const load = useCallback(async () => {
    if (start > end) {
      setStatus("invalid");
      return;
    }
    setStatus("loading");
    try {
      const { data: d } = await api.get("/admin/traffic", { params: { start, end }, headers: headers() });
      setData(d);
      setStatus("ok");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else setStatus("error");
    }
  }, [start, end, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/traffic/export", { params: { start, end }, headers: headers(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trafic_assertimmo_${start}_${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export généré.");
    } catch {
      toast.error("L'export a échoué. Réessayez.");
    }
  };

  return (
    <div className="flex-1 px-6 py-5 space-y-6" data-testid="traffic-dashboard">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          {[["7", "7 jours"], ["30", "30 jours"], ["custom", "Dates libres"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`traffic-period-${k}`}
              onClick={() => choosePreset(k)}
              className={`shrink-0 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                preset === k ? "bg-[#1C1613] text-white" : "border border-[#E6DDD4] text-[#8C7A70] hover:border-[#8C7A70]"
              }`}
            >
              {l}
            </button>
          ))}
          <button onClick={load} data-testid="traffic-refresh-btn" aria-label="Actualiser" className="ml-auto h-9 w-9 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]">
            <RefreshCw strokeWidth={1.5} className="h-4 w-4" />
          </button>
          <button onClick={exportCsv} data-testid="traffic-export-btn" aria-label="Exporter en CSV" className="h-9 w-9 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]">
            <Download strokeWidth={1.5} className="h-4 w-4" />
          </button>
        </div>
        {preset === "custom" && (
          <div className="grid grid-cols-2 gap-3">
            {[["Du", start, setStart, "start"], ["Au", end, setEnd, "end"]].map(([l, v, set, id]) => (
              <label key={id} className="text-xs text-[#8C7A70]">
                {l}
                <input
                  type="date"
                  data-testid={`traffic-date-${id}`}
                  value={v}
                  onChange={(e) => e.target.value && set(e.target.value)}
                  className="mt-1 h-11 w-full bg-white border border-[#E6DDD4] rounded-xl px-3 text-sm text-[#231C18] outline-none focus:border-[#C84C2C]"
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {status === "loading" && <p className="text-center text-sm text-[#8C7A70] py-10">Chargement…</p>}
      {status === "invalid" && (
        <p data-testid="traffic-invalid-range" className="text-sm text-[#C62828] text-center py-6">
          La date « Du » doit précéder la date « Au ».
        </p>
      )}
      {status === "error" && (
        <div className="text-center py-10" data-testid="traffic-error">
          <AlertCircle strokeWidth={1.5} className="h-8 w-8 text-[#C62828] mx-auto mb-2" />
          <p className="text-sm text-[#8C7A70]">Erreur de chargement.</p>
        </div>
      )}
      {status === "ok" && data && (
        <>
          <FunnelTable
            title="Par source"
            testid="traffic-sources"
            rows={data.sources}
            firstCol="Source"
            label={(r) => (r.content ? `${r.source} / ${r.content}` : r.source)}
          />
          <FunnelTable
            title="Par simulateur"
            testid="traffic-simulateurs"
            rows={data.simulateurs}
            firstCol="Simulateur"
            label={(r) => (r.simulateur === "accueil" ? "Accueil" : SIM_LABELS[r.simulateur] || r.simulateur)}
          />
          <DeviceSplit devices={data.devices} />
          <p className="text-[11px] text-[#8C7A70] leading-relaxed">
            Visites comptées une fois par session (onglet) et par étape, indépendamment du consentement cookies.
            Par source : nombre de sessions ; par simulateur : sessions par simulateur.
          </p>
        </>
      )}
    </div>
  );
}
